import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/groupBy";
import { usePermissions } from "@/context/PermissionsContext";
import { OwnerFilter } from "@/components/OwnerFilter";
import {
  LayoutDashboard, Apple, ChefHat, Boxes, ShoppingCart, Receipt, TrendingUp, Package, Warehouse,
  LogOut, Menu, X, FileBarChart, Settings as SettingsIcon, Users as UsersIcon, HeartHandshake, History, BookOpen, Leaf,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Özet & Genel Bakış", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/malzemeler", label: "Malzeme Deposu", icon: Apple, id: "ingredients" },
  { to: "/urunler", label: "Ürün & Reçete", icon: ChefHat, id: "recipes" },
  { to: "/tarifler", label: "Tarifler", icon: BookOpen, id: "guide" },
  { to: "/stok", label: "Stok Takibi", icon: Boxes, id: "stock" },
  { to: "/satis", label: "Kasa & Satış", icon: ShoppingCart, id: "sales" },
  { to: "/perakende", label: "Perakende Satış", icon: Package, id: "retail" },
  { to: "/perakende-stok", label: "Herbalife Stok", icon: Warehouse, id: "retail_stock" },
  { to: "/myherbalife", label: "MyHerbalife", icon: Leaf, id: "myherbalife" },
  { to: "/satis-gecmisi", label: "Satış Geçmişi", icon: History, id: "sales" },
  { to: "/finans", label: "Gelir / Gider", icon: Receipt, id: "finances" },
  { to: "/raporlar", label: "Kâr / Zarar", icon: TrendingUp, id: "reports" },
  { to: "/z-raporu", label: "Günlük Z Raporu", icon: FileBarChart, id: "zreport" },
  { to: "/kulup", label: "Beslenme Kulübü", icon: HeartHandshake, id: "club" },
  { to: "/kullanicilar", label: "Kullanıcılar & Yetkiler", icon: UsersIcon, id: "users", adminOnly: true },
  { to: "/ayarlar", label: "Hesap Ayarları", icon: SettingsIcon, id: "settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { mine, labels } = usePermissions();
  const visibleNav = useMemo(
    () => NAV.filter((n) => user?.role === "admin" || (!n.adminOnly && (mine || []).includes(n.id))),
    [user?.role, mine]
  );
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-dvh w-64 transform flex-col border-r border-border bg-card pt-[env(safe-area-inset-top)] transition-transform duration-300 print:hidden lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex shrink-0 flex-col items-center gap-2 border-b border-border px-5 py-3 lg:py-5">
          <img src="/logo.png" alt="Fit House Drinks & Coffee" data-testid="sidebar-logo" className="h-16 w-auto object-contain drop-shadow-[0_6px_16px_rgba(0,0,0,0.6)] lg:h-28" />
        </div>
        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3" data-testid="sidebar-nav">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 border-t border-border bg-card p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="sidebar-footer">
          <div className="mb-2 flex items-center gap-2.5 px-2">
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-9 w-9 shrink-0 rounded-full object-cover" data-testid="sidebar-avatar" />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-bold" data-testid="sidebar-avatar-initial">{(user?.name || "F")[0].toUpperCase()}</div>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{user?.name || "Yönetici"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-widest text-primary" data-testid="sidebar-role">{roleLabel(user?.role, labels)}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" /> Çıkış Yap
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex min-h-16 items-center justify-between gap-2 border-b border-border bg-background/80 px-4 pt-[env(safe-area-inset-top)] backdrop-blur-md print:hidden lg:px-8">
          <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg active:bg-secondary lg:hidden" onClick={() => setOpen(!open)} aria-label="Menü" data-testid="sidebar-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="hidden lg:block">
            <p className="text-sm text-muted-foreground">Fit House — Drinks & Coffee</p>
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <OwnerFilter />
            <span className="hidden rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary sm:inline">
              Çevrimiçi
            </span>
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full object-cover" data-testid="header-avatar" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-bold">
                {(user?.name || "F")[0].toUpperCase()}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:p-8">{children}</main>
      </div>
    </div>
  );
}
